import { useCallback, useEffect, useState } from 'react';
import { api, type ColorRow } from '../lib/api.js';

interface DraftColor {
  id: string;
  name: string;
  hex: string;
  ring_hex: string;
  display_order: number;
}
const EMPTY_DRAFT: DraftColor = { id: '', name: '', hex: '#000000', ring_hex: '#333333', display_order: 100 };

export function ColorsPage(): JSX.Element {
  const [items, setItems] = useState<ColorRow[]>([]);
  const [draft, setDraft] = useState<DraftColor>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const r = await api.colors.list();
      setItems(r.items);
    } catch (err) {
      setError(String(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <>
      <h1>Colors</h1>
      <p style={{ color: 'var(--muted)' }}>
        Colors that appear in the configurator's color picker. Inactive colors stay in the database
        but are hidden from the widget.
      </p>
      {error && <div className="notice error">{error}</div>}

      <table>
        <thead>
          <tr>
            <th>ID</th><th>Name</th><th>Hex</th><th>Ring</th><th>Order</th><th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td className="sku">{c.id}</td>
              <td>{c.name}</td>
              <td>
                <span style={{ display: 'inline-block', width: 18, height: 18, background: c.hex, border: '1px solid var(--border)', verticalAlign: 'middle', marginRight: 6 }} />
                {c.hex}
              </td>
              <td>
                <span style={{ display: 'inline-block', width: 18, height: 18, background: c.ring_hex, border: '1px solid var(--border)', verticalAlign: 'middle', marginRight: 6 }} />
                {c.ring_hex}
              </td>
              <td>{c.display_order}</td>
              <td>{c.is_active === 0 ? <span className="badge inactive">Inactive</span> : 'Active'}</td>
              <td>
                {c.is_active !== 0 && (
                  <button className="danger" onClick={async () => {
                    if (!confirm(`Hide color ${c.name}?`)) return;
                    await api.colors.softDelete(c.id);
                    void refresh();
                  }}>Hide</button>
                )}
                {c.is_active === 0 && (
                  <button onClick={async () => {
                    await api.colors.update(c.id, { is_active: 1 });
                    void refresh();
                  }}>Re-enable</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={{ marginTop: 32 }}>Add color</h2>
      <div className="fields" style={{ maxWidth: 720 }}>
        <label>ID (short code)
          <input value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value.toUpperCase() })} placeholder="e.g. GRY" />
        </label>
        <label>Display name
          <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Gray" />
        </label>
        <label>Hex (fill)
          <input type="color" value={draft.hex} onChange={(e) => setDraft({ ...draft, hex: e.target.value })} />
        </label>
        <label>Ring hex (selection ring)
          <input type="color" value={draft.ring_hex} onChange={(e) => setDraft({ ...draft, ring_hex: e.target.value })} />
        </label>
        <label>Display order
          <input
            type="number"
            value={draft.display_order}
            onChange={(e) => setDraft({ ...draft, display_order: Number(e.target.value) })}
          />
        </label>
      </div>
      <div style={{ marginTop: 12 }}>
        <button
          className="primary"
          disabled={busy || !draft.id || !draft.name}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.colors.create({
                id: draft.id,
                name: draft.name,
                hex: draft.hex,
                ring_hex: draft.ring_hex,
                display_order: draft.display_order,
              });
              setDraft(EMPTY_DRAFT);
              void refresh();
            } catch (err) {
              setError(String(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Create color'}
        </button>
      </div>
    </>
  );
}
